import { useFetchCollectionList, useFetchUsedImageCollection, usePutImageCollection } from '@api';
import { CheckPrimaryIcon, PlusGray600Icon, XBlackIcon } from '@assets/iconComponents';
import { Modal, SearchBar, SecureImage, Text } from '..';
import { PathnameEnum, styleConstant, ToastMessageEnum } from '@constant';
import { useToast } from '@hooks';
import { Button, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { colorSet } from '@theme';
import { ICollection, IImageListItem } from '@types';
import { AxiosResponse } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';

interface ICollectionSelectModalProps {
  isVisible: boolean;
  onClose: () => void;
  imageId?: number;
  onCollectionUpsertModalOpen: () => void;
  createQueryStringForImageList?: () => string;
  onImageCollected?: (isCollected: boolean) => void;
}

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const CollectionList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  maxHeight: 'calc(100vh - 504px)',
  overflowY: 'auto',
  overflowX: 'hidden',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  },

  '&.show-scrollbar': {
    paddingRight: '8px'
  }
});

export const CollectionSelectModal = (props: ICollectionSelectModalProps) => {
  const { isVisible, onClose, imageId, onCollectionUpsertModalOpen, createQueryStringForImageList, onImageCollected } =
    props;

  const { collectionIdPathParam } = useParams(); // URL 파라미터에서 collectionId 추출

  // 초기 상태를 저장할 state 추가
  const [initialCollectionStates, setInitialCollectionStates] = useState<Record<number, boolean>>({});
  // 컬렉션 이름 검색 관련
  const [inputKeyword, setInputKeyword] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
  // 저장 버튼 disabled 상태
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  // 컬렉션 리스트 스크롤바 여부에 따른 CSS 분기를 위한 상태
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const { showToast } = useToast();

  const collectionListRef = useRef<HTMLDivElement>(null);
  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;
  const queryClient = useQueryClient();
  const location = useLocation();
  const locationPathname = location.pathname;
  const isStandardOutputPage = locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE;
  const isAdultsOnlyOutputPage = locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE;
  const isCollectionImageListPage = /^\/collections\/\d+$/.test(locationPathname); //pathname이 /collections/숫자 패턴과 일치하는지 검사
  const isUsedImageListPage = locationPathname === PathnameEnum.USED_IMAGE_LIST_PAGE;

  const createQueryStringForCollectionList = () => {
    const temp = [];

    if (imageId) {
      temp.push(`&imageId=${imageId}`);
    }

    if (searchKeyword && searchKeyword.trim().length > 0) {
      temp.push(`&searchKeyword=${searchKeyword.trim()}`);
    }

    const qs = temp.join('');
    return qs;
  };

  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = useFetchCollectionList(
    isVisible,
    createQueryStringForCollectionList()
  );

  const { data: usedImageCollectionData } = useFetchUsedImageCollection(
    isVisible &&
      !isLoading &&
      !isFetching &&
      !hasNextPage &&
      (!searchKeyword || searchKeyword.trim().length === 0) &&
      Boolean(imageId),
    imageId
  );

  const flattenCollectionList = () => {
    const dataArrLength = data?.pages.length;
    const flattenedArr: ICollection[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (data.pages[i].data.data.length > 0) {
          data.pages[i].data.data.map((item: ICollection) => {
            flattenedArr.push(item);
          });
        }
        i++;
      }
    }

    // usedImageCollectionData가 있고, 마지막 페이지까지 fetch가 완료되었고, 컬렉션 검색 결과가 아닌 경우
    if (usedImageCollectionData && !hasNextPage && (!searchKeyword || searchKeyword.trim().length === 0)) {
      const usedImageCollection = {
        ...usedImageCollectionData,
        id: -1,
        name: '사용한 이미지',
        isUsedImages: true
      };
      flattenedArr.push(usedImageCollection);
    }

    return flattenedArr;
  };

  const collectionListQueryString = createQueryStringForCollectionList();
  const collectionListQueryKey = ['get', `/collections${collectionListQueryString}`];

  const { mutateAsync, isPending } = usePutImageCollection(
    // onSuccess 콜백
    (response, variables) => {
      const isCollectedFromResponse = response.data.data.isCollected;

      // 이미지 리스트 queryKey 구성
      let imageListQueryKey: string[];

      // 유저가 접속해있는 페이지에 따라 캐시 업데이트할 데이터를 달리 한다
      if ((isStandardOutputPage || isAdultsOnlyOutputPage) && createQueryStringForImageList) {
        imageListQueryKey = ['get', `/images${createQueryStringForImageList()}`];
      } else if (isCollectionImageListPage) {
        imageListQueryKey = ['get', `/collections/${collectionIdPathParam}`];
      } else if (isUsedImageListPage) {
        imageListQueryKey = ['get', `/images/used-images`];
      } else {
        imageListQueryKey = [];
      }

      // 이미지 리스트 캐시 업데이트
      if (imageListQueryKey && imageListQueryKey.length > 0) {
        queryClient.setQueryData(imageListQueryKey, (oldData: any) => {
          if (!oldData?.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((image: IImageListItem) =>
                  image.id === variables.imageId ? { ...image, isCollected: isCollectedFromResponse } : image
                )
              }
            }))
          };
        });
      }

      // 이미지 모달 상태 업데이트
      onImageCollected?.(isCollectedFromResponse);

      onClose();
    },
    // onError 콜백
    (err) => {
      // 실패 시 이전 상태로 복구
      const previousData = queryClient.getQueryData<AxiosResponse>(collectionListQueryKey);

      if (previousData?.data?.data) {
        // 실패한 요청의 collectionId 추출
        const failedCollectionId = Number(err.config?.url?.split('/').pop());

        // 기존 데이터에서 해당 컬렉션의 isImageIncluded 값을 원래대로 복구
        const restoredCollections = previousData.data.data.map((collection: ICollection) =>
          collection.id === failedCollectionId
            ? { ...collection, isImageIncluded: !collection.isImageIncluded } // 현재 상태의 반대로 복구
            : collection
        );

        // 전체 응답 구조를 유지하면서 collections 데이터만 업데이트
        const restoredData = {
          ...previousData,
          data: {
            ...previousData.data,
            data: restoredCollections
          }
        };

        // 캐시 업데이트
        queryClient.setQueryData(collectionListQueryKey, restoredData);
      }
    }
  );

  // 엔터 입력 시 검색 요청
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Enter': {
        handleSubmit();
        break;
      }
      default: {
        return;
      }
    }
  };

  // 검색 인풋 키워드 변경시
  const handleInputKeywordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputKeyword(e.target.value);
  };

  // 검색 키워드 지우기
  const handleClear = () => {
    setInputKeyword('');
    setSearchKeyword(null);
  };

  // 검색 요청
  const handleSubmit = () => {
    setSearchKeyword(inputKeyword);
  };

  // 컬렉션 리스트 아이템 클릭시 체크 추가/해제
  const handleCollectionListItemClick = (collectionId: number) => {
    if (data) {
      const previousData = queryClient.getQueryData<any>(collectionListQueryKey);

      if (previousData?.pages) {
        // 기존 데이터에서 해당 컬렉션의 isImageIncluded 값만 변경
        const updatedPages = previousData.pages.map((page: any) => ({
          ...page,
          data: {
            ...page.data,
            data: page.data.data.map((collection: ICollection) =>
              collection.id === collectionId
                ? { ...collection, isImageIncluded: !collection.isImageIncluded }
                : collection
            )
          }
        }));

        // 캐시 업데이트
        queryClient.setQueryData(collectionListQueryKey, {
          ...previousData,
          pages: updatedPages
        });
      }
    }
  };

  // 저장 버튼 클릭 핸들러 수정
  const handleSaveButtonClick = () => {
    if (isPending || !data?.pages || !imageId) return;

    const addImageList: { id: number; name: string }[] = [];
    const deleteImageList: { id: number; name: string }[] = [];

    // 현재 상태와 초기 상태 비교
    Object.entries(initialCollectionStates).forEach(([collectionId, initialState]) => {
      // 현재 상태 확인 로직
      const currentCollection = data.pages
        .find((page: any) => {
          return page.data.data.find((c: ICollection) => c.id === Number(collectionId));
        })
        ?.data.data.find((c: ICollection) => c.id === Number(collectionId));

      if (currentCollection) {
        const currentState = currentCollection.isImageIncluded ?? false;

        if (initialState === false && currentState === true) {
          addImageList.push({ id: currentCollection.id, name: currentCollection.name });
        } else if (initialState === true && currentState === false) {
          deleteImageList.push({ id: currentCollection.id, name: currentCollection.name });
        }
      }
    });

    // PUT 요청 실행
    mutateAsync({
      imageId: imageId,
      body: {
        addImage: addImageList.map((item) => item.id),
        deleteImage: deleteImageList.map((item) => item.id)
      }
    }).then(() => {
      if (addImageList.length > 0) {
        showToast(
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Text variant="body1" color={colorSet.white} fontWeight="bold">
              {addImageList.length <= 2
                ? addImageList.map((item) => item.name).join(', ')
                : `${addImageList
                    .slice(0, 2)
                    .map((item) => item.name)
                    .join(', ')} 외 ${addImageList.length - 2}개`}
            </Text>
            <Text variant="body1" color={colorSet.white}>
              {ToastMessageEnum.ADD_COLLECTION_IMAGE}
            </Text>
          </div>
        );
      }
      if (deleteImageList.length > 0) {
        showToast(
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Text variant="body1" color={colorSet.white} fontWeight="bold">
              {deleteImageList.length <= 2
                ? deleteImageList.map((item) => item.name).join(', ')
                : `${deleteImageList
                    .slice(0, 2)
                    .map((item) => item.name)
                    .join(', ')} 외 ${deleteImageList.length - 2}개`}
            </Text>
            <Text variant="body1" color={colorSet.white}>
              {ToastMessageEnum.DELETE_COLLECTION_IMAGE}
            </Text>
          </div>
        );
      }
    });
  };

  // 컬렉션 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  // 모달이 열릴 때 초기화
  useEffect(() => {
    if (isVisible) {
      setInputKeyword('');
      setSearchKeyword(null);
      setInitialCollectionStates({});
    }
  }, [isVisible]);

  // 컬렉션 리스트 데이터가 로드될 때 초기 상태 저장
  useEffect(() => {
    if (data?.pages) {
      const newInitialStates: Record<number, boolean> = {};
      data.pages.forEach((page: any) => {
        page.data.data.forEach((collection: ICollection) => {
          // 이미 저장된 초기값이 있다면 유지
          if (!(collection.id in initialCollectionStates)) {
            newInitialStates[collection.id] = collection.isImageIncluded ?? false;
          }
        });
      });
      setInitialCollectionStates((prev) => ({ ...prev, ...newInitialStates }));
    }
  }, [data]);

  // 저장 버튼 disabled 로직
  useEffect(() => {
    if (!data?.pages) return;

    const hasChanges = Object.entries(initialCollectionStates).some(([collectionId, initialState]) => {
      const currentState = data.pages.some((page: any) => {
        const collection = page.data.data.find((c: ICollection) => c.id === Number(collectionId));
        return collection?.isImageIncluded ?? false;
      });
      return initialState !== currentState;
    });

    setIsSaveButtonDisabled(!hasChanges);
  }, [data, initialCollectionStates]);

  // 컬렉션 리스트의 스크롤바 존재 여부 체크 (isVisible 및 data 변동시)
  useEffect(() => {
    const checkScrollbar = () => {
      if (collectionListRef.current) {
        const element = collectionListRef.current;
        const hasVerticalScrollbar = element.scrollHeight > element.clientHeight;
        setHasScrollbar(hasVerticalScrollbar);
      }
    };

    // 초기 체크를 약간 지연시켜 DOM이 완전히 렌더링된 후 실행
    const timeoutId = setTimeout(checkScrollbar, 100);

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', checkScrollbar);

    // MutationObserver로 DOM 변화 감지
    const observer = new MutationObserver(checkScrollbar);
    if (collectionListRef.current) {
      observer.observe(collectionListRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScrollbar);
      observer.disconnect();
    };
  }, [isVisible, data]);

  return (
    <Modal
      open={isVisible}
      maxWidth={512}
      onClose={onClose}
      sx={{ '& .MuiModal': { zIndex: styleConstant.zIndexModal } }}
    >
      <div style={{ width: 392 }}>
        <XBlackIcon
          style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
          onClick={onClose}
        />

        <Text variant="h3" fontWeight="bold" marginBottom="32px">
          컬렉션에 추가
        </Text>

        <SearchBar
          placeholder="검색어를 입력해주세요."
          value={inputKeyword}
          options={[]}
          onKeyDown={handleKeyDown}
          onInputChange={handleInputKeywordChange}
          onClear={handleClear}
          onSubmit={handleSubmit}
          boxstyle={{ width: 392, marginBottom: '32px' }}
        />

        <div
          onClick={onCollectionUpsertModalOpen}
          style={{
            width: 392,
            height: 52,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: `1px solid ${colorSet.gray300}`,
            backgroundColor: colorSet.gray100,
            cursor: 'pointer',
            marginBottom: '32px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <PlusGray600Icon style={{ width: 20, height: 20 }} />
            <Text variant="body1">새 컬렉션 추가</Text>
          </div>
        </div>

        <CollectionList ref={collectionListRef} className={hasScrollbar ? 'show-scrollbar' : ''}>
          {data &&
            flattenCollectionList().length > 0 &&
            flattenCollectionList().map((item: ICollection) => {
              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (item.isUsedImages) {
                      return;
                    } else {
                      handleCollectionListItemClick(item.id);
                    }
                  }}
                  style={{
                    width: '100%',
                    height: 76,
                    display: 'flex',
                    border: `1px solid ${colorSet.gray300}`,
                    backgroundColor: item.isUsedImages ? colorSet.gray100 : 'transparent',
                    cursor: item.isUsedImages ? 'default' : 'pointer'
                  }}
                >
                  {item.thumbnails && item.thumbnails.length > 0 && item.thumbnails[0] ? (
                    <SecureImage
                      imageUrl={item.thumbnails[0].s3Url}
                      imageStyle={{ width: 88, height: 74, objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ width: 88, height: 74, backgroundColor: colorSet.gray200 }} />
                  )}
                  <div
                    style={{
                      width: 'calc(100% - 88px)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '18px 20px'
                    }}
                  >
                    <div
                      style={{
                        width: item.isImageIncluded ? 'calc(100% - 36px)' : '100%', // 텍스트 ellipsis를 위해 width 적용
                        height: '40px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Text
                        variant="subtitle"
                        fontWeight="bold"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.name}
                      </Text>
                      <Text variant="body3">{item.imageCount ? item.imageCount.toLocaleString() : 0}장</Text>
                    </div>
                    {item.isImageIncluded ? (
                      <div>
                        <CheckPrimaryIcon
                          style={{
                            width: 20,
                            height: 20,
                            marginLeft: '16px',
                            marginBottom: '2px'
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          <div ref={outputBottomRef} />
        </CollectionList>

        <div style={{ marginTop: 40, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <Button
            disabled={isSaveButtonDisabled}
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSaveButtonClick}
          >
            저장
          </Button>
        </div>
      </div>
    </Modal>
  );
};
